import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import ShopVerificationNotification from './ShopVerificationNotification'

const ShopSetupNotification = ({ onDismiss, showSuccess = false }) => {
  const navigate = useNavigate()
  const { userType, activeSubscription } = useAuth()
  const location = useLocation()

  const { data: shopData, isLoading } = useQuery({
    queryKey: QueryKeys.myShopDetails(userType),
    queryFn: () => handleGetRequest(`${userType}s/my-shop`),
  })

  if (isLoading) {
    return null
  }

  const shop = shopData?.data

  const checkShopSetupStatus = () => {
    if (!shop) return { isComplete: false, issues: ['Shop data not available'] }

    const issues = []

    if (!shop.countryId) {
      issues.push('Country not set')
    }

    if (!shop.cityId) {
      issues.push('City not set')
    }

    if (!shop.shopPicture) {
      issues.push('Shop picture not uploaded')
    }

    if (!activeSubscription?.id) {
      issues.push('No active subscription found. Please subscribe to a plan.')
    }

    return {
      isComplete: issues.length === 0,
      issues,
    }
  }

  const { isComplete, issues } = checkShopSetupStatus()

  // Show success message if setup is complete and showSuccess is true
  if (isComplete && showSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 shadow-sm">
        <div className="flex items-center space-x-3">
          <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-green-800">Shop Setup Complete!</h3>
            <p className="text-sm text-green-700">
              Your shop is fully set up and operational. You're ready to start selling!
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-green-600 hover:text-green-800 p-1"
              aria-label="Dismiss notification"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    )
  }

  const getNotificationAction = () => {
    if (location.pathname === '/subscription' && !activeSubscription?.id) return

    if (!activeSubscription?.id) {
      return (
        <button
          onClick={() => navigate('/subscription')}
          className="bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-amber-700 transition-colors"
        >
          Subscribe Now
        </button>
      )
    }

    if (location.pathname === '/my-shop') return

    return (
      <button
        onClick={() => navigate('/my-shop')}
        className="bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-amber-700 transition-colors"
      >
        Go to My Shop & Setup
      </button>
    )
  }

  if (isComplete && !['/', '/my-shop'].includes(location.pathname)) {
    return
  }

  if (isComplete && ['/', '/my-shop'].includes(location.pathname)) {
    return <ShopVerificationNotification />
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4  shadow-sm w-full">
      <div className="flex items-start space-x-3">
        <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />

        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">Complete Your Shop Setup</h3>

          <p className="text-sm text-amber-700 mb-3">
            Your shop setup is not yet complete. Please address the following issues to ensure your
            shop is ready for customers:
          </p>

          <ul className="text-sm text-amber-700 space-y-1 mb-4">
            {issues.map((issue, index) => (
              <li key={index} className="flex items-center space-x-2">
                <XMarkIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span>{issue}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between">
            {getNotificationAction()}

            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-amber-600 hover:text-amber-800 p-1"
                aria-label="Dismiss notification"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShopSetupNotification
